import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BellRing, Search, Plus, Trash2, Send, AlertCircle, CheckCircle2,
  Clock, Package, Users, X, RefreshCw, History,
} from 'lucide-react'
import { stockAlertsApi, WaitlistItem, WaitlistProductSearchResult } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

// ============================================================================
// Helpers
// ============================================================================

const SHOP_BASE = 'https://www.jvs-informatica.com'

const productImageUrl = (id?: number | null) => {
  if (!id) return `${SHOP_BASE}/img/p/es-default-cart_default.jpg`
  return `${SHOP_BASE}/img/p/${String(id).split('').join('/')}/${id}-cart_default.jpg`
}

const STATUS_META: Record<WaitlistItem['status'], { label: string; cls: string }> = {
  waiting: { label: 'Esperando reposición', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  ready_to_notify: { label: 'Stock disponible — pendiente notificar', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  under_reserve: { label: 'Sin stock (reservable)', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  orphan: { label: 'Producto inactivo/eliminado', cls: 'bg-slate-200 text-slate-600 border-slate-300' },
}

const LANG_OPTIONS = [
  { value: 1, label: 'Español' },
  { value: 2, label: 'Português' },
  { value: 3, label: 'English' },
  { value: 4, label: 'Français' },
  { value: 5, label: 'Deutsch' },
]

// ============================================================================
// Página principal
// ============================================================================

export default function StockAlertsPage() {
  const toast = useToast()
  const qc = useQueryClient()

  const [tab, setTab] = useState<'list' | 'audit'>('list')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [showAdd, setShowAdd] = useState(false)
  const [subscribersFor, setSubscribersFor] = useState<WaitlistItem | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const { data: summary } = useQuery({
    queryKey: ['stock-alerts', 'summary'],
    queryFn: () => stockAlertsApi.summary(),
  })

  const { data: pending, isLoading: loadingList } = useQuery({
    queryKey: ['stock-alerts', 'pending', debouncedSearch, statusFilter, page],
    queryFn: () => stockAlertsApi.listPending({
      q: debouncedSearch || undefined,
      status: statusFilter || undefined,
      page,
      page_size: 50,
    }),
    enabled: tab === 'list',
  })

  const { data: audit } = useQuery({
    queryKey: ['stock-alerts', 'audit'],
    queryFn: () => stockAlertsApi.audit(200, 0),
    enabled: tab === 'audit',
  })

  const notifyMut = useMutation({
    mutationFn: (id_product: number) => stockAlertsApi.notifyNow(id_product, 'Disparo manual desde panel'),
    onSuccess: (data) => {
      toast.success(`Notificación enviada: ${data.notified} email(s)`)
      qc.invalidateQueries({ queryKey: ['stock-alerts'] })
    },
    onError: (e: any) => toast.error('Error al notificar', e?.response?.data?.detail || e.message),
  })

  const cleanupMut = useMutation({
    mutationFn: () => stockAlertsApi.cleanupOrphans(),
    onSuccess: (data) => {
      toast.success(`Limpieza completada: ${data.deleted} avisos huérfanos eliminados`)
      qc.invalidateQueries({ queryKey: ['stock-alerts'] })
    },
    onError: (e: any) => toast.error('Error en limpieza', e?.response?.data?.detail || e.message),
  })

  const totalPages = pending ? Math.max(1, Math.ceil(pending.total / pending.page_size)) : 1

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <BellRing className="w-7 h-7 text-blue-600" />
            Lista de espera
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Clientes que han solicitado aviso cuando un producto vuelva a estar disponible.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => cleanupMut.mutate()}
            disabled={cleanupMut.isPending}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${cleanupMut.isPending ? 'animate-spin' : ''}`} />
            Limpiar huérfanos
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Añadir manual
          </button>
        </div>
      </div>

      {/* Métricas */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard icon={<Users className="w-5 h-5" />} label="Avisos totales" value={summary.total_alerts} accent="blue" />
          <MetricCard icon={<Package className="w-5 h-5" />} label="Productos en espera" value={summary.total_products} accent="indigo" />
          <MetricCard icon={<CheckCircle2 className="w-5 h-5" />} label="Listos para notificar" value={summary.ready_to_notify} accent="emerald" />
          <MetricCard icon={<Clock className="w-5 h-5" />} label="Esperando reposición" value={summary.waiting} accent="amber" />
        </div>
      )}

      {/* Top demanda */}
      {summary && summary.top_demand.length > 0 && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            Productos más demandados
          </h2>
          <div className="flex flex-wrap gap-2">
            {summary.top_demand.map((p) => (
              <a
                key={p.id_product}
                href={`https://www.jvs-informatica.com/inicio/${p.id_product}.html`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-900 rounded-full text-xs hover:bg-slate-200 dark:hover:bg-slate-700"
                title={p.name}
              >
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">{p.reference}</span>
                <span className="bg-blue-600 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">{p.subscribers}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex gap-1">
          <TabButton active={tab === 'list'} onClick={() => setTab('list')}>Lista de espera</TabButton>
          <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>
            <History className="w-4 h-4 inline mr-1" />
            Auditoría
          </TabButton>
        </nav>
      </div>

      {tab === 'list' && (
        <>
          {/* Filtros */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex-1 min-w-[240px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por referencia, nombre o email..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
            >
              <option value="">Todos los estados</option>
              <option value="waiting">Esperando reposición</option>
              <option value="ready_to_notify">Stock disponible — pendiente</option>
              <option value="under_reserve">Sin stock (reservable)</option>
              <option value="orphan">Productos huérfanos</option>
            </select>
          </div>

          {/* Tabla */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Producto</th>
                  <th className="text-left px-4 py-3 font-medium">Stock</th>
                  <th className="text-left px-4 py-3 font-medium">Suscriptores</th>
                  <th className="text-left px-4 py-3 font-medium">Estado</th>
                  <th className="text-right px-4 py-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {loadingList && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">Cargando...</td></tr>
                )}
                {!loadingList && pending && pending.items.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                    No hay avisos {debouncedSearch || statusFilter ? 'con esos filtros' : 'pendientes'}.
                  </td></tr>
                )}
                {pending?.items.map((item) => (
                  <tr key={`${item.id_product}-${item.id_product_attribute}`} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={productImageUrl(item.cover_image_id)}
                          alt=""
                          className="w-12 h-12 rounded object-contain bg-slate-100 dark:bg-slate-900 flex-shrink-0"
                          onError={(e) => { (e.target as HTMLImageElement).src = `${SHOP_BASE}/img/p/es-default-cart_default.jpg` }}
                        />
                        <div className="min-w-0">
                          <div className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                            {item.reference || `#${item.id_product}`}
                          </div>
                          <div className="text-slate-900 dark:text-slate-100 truncate max-w-md">
                            {item.name || <span className="italic text-slate-400">Producto eliminado</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.stock === null ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <span className={`font-semibold ${item.stock > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                          {item.stock}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs font-bold">
                        {item.subscribers}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-md text-[11px] font-medium border ${STATUS_META[item.status].cls}`}>
                        {STATUS_META[item.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {item.status === 'ready_to_notify' && (
                          <button
                            onClick={() => notifyMut.mutate(item.id_product)}
                            disabled={notifyMut.isPending}
                            className="px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded inline-flex items-center gap-1"
                            title="Enviar email a los suscriptores ahora"
                          >
                            <Send className="w-3 h-3" /> Notificar
                          </button>
                        )}
                        <button
                          onClick={() => setSubscribersFor(item)}
                          className="px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-100 dark:hover:bg-slate-700 inline-flex items-center gap-1"
                        >
                          <Users className="w-3 h-3" /> Ver
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginación */}
            {pending && pending.total > pending.page_size && (
              <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-sm">
                <span className="text-slate-500">
                  {pending.total} avisos · página {page} de {totalPages}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    className="px-3 py-1 border border-slate-300 dark:border-slate-600 rounded disabled:opacity-50">Anterior</button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                    className="px-3 py-1 border border-slate-300 dark:border-slate-600 rounded disabled:opacity-50">Siguiente</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'audit' && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Fecha</th>
                <th className="text-left px-4 py-3 font-medium">Acción</th>
                <th className="text-left px-4 py-3 font-medium">Usuario</th>
                <th className="text-left px-4 py-3 font-medium">Email cliente</th>
                <th className="text-left px-4 py-3 font-medium">Producto</th>
                <th className="text-left px-4 py-3 font-medium">Motivo</th>
                <th className="text-left px-4 py-3 font-medium">Resultado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {audit?.items.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/50">
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{row.performed_at}</td>
                  <td className="px-4 py-2"><AuditActionBadge action={row.action} /></td>
                  <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{row.performed_by}</td>
                  <td className="px-4 py-2 text-slate-600 dark:text-slate-400">{row.customer_email || '—'}</td>
                  <td className="px-4 py-2">
                    {row.id_product ? (
                      <div>
                        <div className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                          {row.reference || `#${row.id_product}`}
                        </div>
                        {row.reference && (
                          <div className="text-[10px] text-slate-400">#{row.id_product}</div>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-2 text-slate-500 italic">{row.reason || '—'}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 font-mono">
                    {row.result ? JSON.stringify(row.result) : '—'}
                  </td>
                </tr>
              ))}
              {audit && audit.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Sin acciones registradas todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modales */}
      {showAdd && <AddManualModal onClose={() => setShowAdd(false)} />}
      {subscribersFor && (
        <SubscribersModal item={subscribersFor} onClose={() => setSubscribersFor(null)} />
      )}
    </div>
  )
}

// ============================================================================
// Componentes auxiliares
// ============================================================================

function MetricCard({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: number; accent: 'blue' | 'indigo' | 'emerald' | 'amber' }) {
  const accents: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    indigo: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
    emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  }
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${accents[accent]}`}>{icon}</div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</div>
        </div>
      </div>
    </div>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-blue-600 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
      }`}
    >
      {children}
    </button>
  )
}

function AuditActionBadge({ action }: { action: string }) {
  const meta: Record<string, { label: string; cls: string }> = {
    manual_add: { label: 'Alta manual', cls: 'bg-emerald-100 text-emerald-700' },
    manual_delete: { label: 'Borrado', cls: 'bg-rose-100 text-rose-700' },
    manual_notify: { label: 'Notificación', cls: 'bg-blue-100 text-blue-700' },
    cleanup: { label: 'Limpieza', cls: 'bg-slate-200 text-slate-700' },
  }
  const m = meta[action] || { label: action, cls: 'bg-slate-100 text-slate-600' }
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${m.cls}`}>{m.label}</span>
}

// ============================================================================
// Modal: Añadir manual
// ============================================================================

function AddManualModal({ onClose }: { onClose: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()

  const [email, setEmail] = useState('')
  const [productQuery, setProductQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<WaitlistProductSearchResult | null>(null)
  const [showProductDropdown, setShowProductDropdown] = useState(false)
  const [idLang, setIdLang] = useState(1)
  const [reason, setReason] = useState('')
  const [confirmStockOpen, setConfirmStockOpen] = useState<{ has_stock: boolean; quantity: number; reference: string } | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(productQuery), 250)
    return () => clearTimeout(t)
  }, [productQuery])

  const { data: searchResults, isFetching } = useQuery({
    queryKey: ['stock-alerts', 'search', debouncedQuery],
    queryFn: () => stockAlertsApi.searchProducts(debouncedQuery),
    enabled: debouncedQuery.length >= 2 && !selectedProduct,
  })

  const submitAlert = useMutation({
    mutationFn: (notify_immediately: boolean) =>
      stockAlertsApi.addManual({
        email,
        id_product: selectedProduct!.id_product,
        id_lang: idLang,
        reason: reason || undefined,
        notify_immediately,
      }),
    onSuccess: (data) => {
      if (data.created) {
        if (data.notified) {
          toast.success('Aviso creado y email enviado al cliente')
        } else {
          toast.success('Aviso creado correctamente')
        }
      } else if (data.reason === 'already_exists') {
        toast.error('Ya existe un aviso para ese cliente y producto')
      }
      qc.invalidateQueries({ queryKey: ['stock-alerts'] })
      onClose()
    },
    onError: (e: any) => toast.error('Error', e?.response?.data?.detail || e.message),
  })

  const handleSubmit = async () => {
    if (!email || !selectedProduct) return
    try {
      const stock = await stockAlertsApi.checkStock(selectedProduct.id_product)
      if (stock.has_stock) {
        setConfirmStockOpen({ has_stock: true, quantity: stock.quantity, reference: stock.reference })
        return
      }
      submitAlert.mutate(false)
    } catch (e: any) {
      toast.error('No se pudo comprobar el stock', e?.response?.data?.detail || e.message)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BellRing className="w-5 h-5 text-blue-600" />
            Añadir cliente a la lista de espera
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Email del cliente <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="cliente@ejemplo.com"
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Producto <span className="text-red-500">*</span>
            </label>
            {selectedProduct ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900">
                <div className="flex items-center gap-3 min-w-0">
                  <img src={productImageUrl(selectedProduct.cover_image_id)} alt="" className="w-10 h-10 object-contain rounded bg-white" />
                  <div className="min-w-0">
                    <div className="font-mono text-xs font-semibold">{selectedProduct.reference}</div>
                    <div className="text-sm truncate">{selectedProduct.name}</div>
                  </div>
                </div>
                <button onClick={() => { setSelectedProduct(null); setProductQuery('') }} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={productQuery}
                    onChange={(e) => { setProductQuery(e.target.value); setShowProductDropdown(true) }}
                    onFocus={() => setShowProductDropdown(true)}
                    placeholder="Referencia o nombre..."
                    className="w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm"
                  />
                </div>
                {showProductDropdown && debouncedQuery.length >= 2 && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {isFetching && <div className="px-3 py-2 text-sm text-slate-500">Buscando...</div>}
                    {!isFetching && searchResults && searchResults.items.length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-500">Sin resultados.</div>
                    )}
                    {searchResults?.items.map((p) => (
                      <button
                        key={p.id_product}
                        onClick={() => { setSelectedProduct(p); setShowProductDropdown(false) }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                      >
                        <img src={productImageUrl(p.cover_image_id)} alt="" className="w-8 h-8 object-contain rounded bg-slate-100" />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-xs font-semibold">{p.reference}</div>
                          <div className="text-sm truncate">{p.name}</div>
                        </div>
                        <span className={`text-xs ${(p.stock ?? 0) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {p.stock ?? 0} ud
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Idioma del email</label>
            <select
              value={idLang}
              onChange={(e) => setIdLang(Number(e.target.value))}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm"
            >
              {LANG_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Motivo (opcional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Cliente llamó por teléfono, vino por WhatsApp..."
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm resize-none"
            />
          </div>
        </div>

        <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!email || !selectedProduct || submitAlert.isPending}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
          >
            {submitAlert.isPending ? 'Guardando...' : 'Añadir aviso'}
          </button>
        </div>
      </div>

      {confirmStockOpen && (
        <ConfirmStockDialog
          info={confirmStockOpen}
          onCancel={() => setConfirmStockOpen(null)}
          onNotifyNow={() => { setConfirmStockOpen(null); submitAlert.mutate(true) }}
          onJustRegister={() => { setConfirmStockOpen(null); submitAlert.mutate(false) }}
        />
      )}
    </div>
  )
}

function ConfirmStockDialog({
  info, onCancel, onNotifyNow, onJustRegister,
}: {
  info: { quantity: number; reference: string }
  onCancel: () => void
  onNotifyNow: () => void
  onJustRegister: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-5">
        <div className="flex gap-3 mb-3">
          <div className="p-2 bg-amber-100 text-amber-700 rounded-lg flex-shrink-0">
            <AlertCircle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">El producto ya tiene stock</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              <span className="font-mono font-semibold">{info.reference}</span> tiene actualmente <strong>{info.quantity}</strong> unidades disponibles.
              ¿Quieres notificar al cliente ahora o registrar el aviso para la próxima rotura de stock?
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-4">
          <button onClick={onNotifyNow} className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm flex items-center justify-center gap-2">
            <Send className="w-4 h-4" /> Notificar ahora por email
          </button>
          <button onClick={onJustRegister} className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg text-sm">
            Solo registrar (esperar próxima reposición)
          </button>
          <button onClick={onCancel} className="w-full px-4 py-2 text-sm text-slate-500 hover:text-slate-700">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Modal: Suscriptores de un producto
// ============================================================================

function SubscribersModal({ item, onClose }: { item: WaitlistItem; onClose: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['stock-alerts', 'subscribers', item.id_product, item.id_product_attribute],
    queryFn: () => stockAlertsApi.getSubscribers(item.id_product, item.id_product_attribute),
  })

  const deleteMut = useMutation({
    mutationFn: (email: string) => stockAlertsApi.deleteAlert({
      email,
      id_product: item.id_product,
      id_product_attribute: item.id_product_attribute,
      reason: 'Borrado manual desde panel',
    }),
    onSuccess: () => {
      toast.success('Aviso eliminado')
      qc.invalidateQueries({ queryKey: ['stock-alerts'] })
    },
    onError: (e: any) => toast.error('Error', e?.response?.data?.detail || e.message),
  })

  const langName = (id: number) => LANG_OPTIONS.find(o => o.value === id)?.label || `id=${id}`

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold truncate">Suscriptores de {item.reference}</h2>
            <p className="text-sm text-slate-500 truncate">{item.name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {isLoading && <div className="text-center text-slate-400 py-8">Cargando...</div>}
          {data && data.items.length === 0 && (
            <div className="text-center text-slate-400 py-8">No hay suscriptores.</div>
          )}
          {data && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Email</th>
                  <th className="text-left px-3 py-2 font-medium">Idioma</th>
                  <th className="text-left px-3 py-2 font-medium">Cliente</th>
                  <th className="text-right px-3 py-2 font-medium">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {data.items.map((s) => (
                  <tr key={`${s.email}-${s.id_lang}`}>
                    <td className="px-3 py-2 text-slate-800 dark:text-slate-200">{s.email}</td>
                    <td className="px-3 py-2 text-slate-500">{langName(s.id_lang)}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {s.id_customer > 0 ? `#${s.id_customer}` : 'Invitado'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => {
                          if (confirm(`¿Eliminar el aviso de ${s.email}?`)) deleteMut.mutate(s.email)
                        }}
                        disabled={deleteMut.isPending}
                        className="p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded"
                        title="Eliminar aviso"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
